import React from 'react';
import { Check } from 'lucide-react';

// Bottom sheet animation styles
export const bottomSheetStyles = `
  @keyframes slideUpSheet {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
  @keyframes fadeInOverlay {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

interface BottomSheetOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  description?: string;
}

interface MobileBottomSheetProps<T extends string> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  options: BottomSheetOption<T>[];
  selectedValue?: T;
  onSelect: (value: T) => void;
  variant?: 'simple' | 'detailed';
}

export function MobileBottomSheet<T extends string>({
  isOpen,
  onClose,
  title,
  options,
  selectedValue,
  onSelect,
  variant = 'simple',
}: MobileBottomSheetProps<T>) {
  if (!isOpen) return null;

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/40 z-[100]"
        style={{ animation: 'fadeInOverlay 200ms ease-out' }}
      />
      <div
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[20px] z-[101] pb-safe-6"
        style={{ animation: 'slideUpSheet 300ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-9 h-1 bg-gray-300 rounded-full" />
        </div>
        <div className="px-5 pb-2">
          <h3 className="text-[17px] font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="px-3">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onSelect(option.value);
                onClose();
              }}
              className={`w-full flex items-center ${variant === 'detailed' ? 'gap-4 px-4 py-4' : 'justify-between px-4 py-3.5'} text-left active:bg-gray-50 rounded-xl`}
            >
              {variant === 'detailed' && option.icon && (
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  {option.icon}
                </div>
              )}
              {variant === 'detailed' ? (
                <div>
                  <p className="text-[15px] font-medium text-gray-900">{option.label}</p>
                  {option.description && (
                    <p className="text-[13px] text-gray-500">{option.description}</p>
                  )}
                </div>
              ) : (
                <span className={`text-[15px] ${selectedValue === option.value ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                  {option.label}
                </span>
              )}
              {variant === 'simple' && selectedValue === option.value && (
                <Check className="w-5 h-5 text-gray-900" />
              )}
            </button>
          ))}
        </div>
        <div className="px-5 pt-3">
          <button
            onClick={onClose}
            className="w-full py-3.5 bg-gray-100 text-[15px] font-medium text-gray-700 rounded-xl active:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
